/**
 * TeamMemberPicker — multi-select for AMC team members.
 * Used by the email composer (Drawdown Emails / Reminders / Acknowledgments)
 * to choose teammates to add to Cc or Bcc on every recipient's email.
 *
 * Data source: GET /api/team-members (excludes the current user, excludes
 * inactive accounts, includes name + email + role).
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { Search, Check, Users, X, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamMemberPicker = ({
  label,
  value = [],            // array of selected user_ids
  onChange,              // (next: string[]) => void
  placeholder = 'Add teammates…',
  testid = 'team-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/team-members`);
        if (mounted) setMembers(data || []);
      } catch {
        if (mounted) setMembers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const byId = useMemo(() => {
    const m = new Map();
    members.forEach(u => m.set(u.id, u));
    return m;
  }, [members]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return members;
    return members.filter(u =>
      (u.name || '').toLowerCase().includes(needle) ||
      (u.email || '').toLowerCase().includes(needle)
    );
  }, [members, q]);

  const toggle = (id) => {
    const next = value.includes(id) ? value.filter(x => x !== id) : [...value, id];
    onChange?.(next);
  };

  const remove = (id, e) => {
    e?.stopPropagation();
    onChange?.(value.filter(x => x !== id));
  };

  const selectedChips = value
    .map(id => byId.get(id))
    .filter(Boolean);

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      )}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full min-h-[40px] px-2.5 py-1.5 border border-gray-300 rounded-lg bg-white text-left flex items-center flex-wrap gap-1.5 hover:border-gray-400 focus:ring-2 focus:ring-blue-500"
        data-testid={`${testid}-toggle`}
      >
        {selectedChips.length === 0 && (
          <span className="text-xs text-gray-400 flex-1 flex items-center">
            <Users size={13} className="mr-1.5" /> {placeholder}
          </span>
        )}
        {selectedChips.map(u => (
          <span
            key={u.id}
            className="inline-flex items-center text-xs bg-blue-50 text-blue-800 border border-blue-200 rounded px-1.5 py-0.5 max-w-[260px]"
            title={u.email}
          >
            <span className="truncate">{u.name}</span>
            <button
              type="button"
              onClick={(e) => remove(u.id, e)}
              className="ml-1 text-blue-500 hover:text-blue-800"
              aria-label={`Remove ${u.name}`}
              data-testid={`${testid}-chip-remove-${u.id}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
        <ChevronDown size={14} className="ml-auto text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                data-testid={`${testid}-search`}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <p className="p-3 text-xs text-gray-500 italic text-center">Loading teammates…</p>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-xs text-gray-500 italic text-center">
                {members.length === 0 ? 'No other AMC users found.' : 'No matches.'}
              </p>
            ) : filtered.map(u => {
              const isSel = value.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className={`w-full text-left px-2.5 py-1.5 text-sm flex items-center hover:bg-blue-50 ${isSel ? 'bg-blue-50/60' : ''}`}
                  data-testid={`${testid}-option-${u.id}`}
                >
                  <span className={`w-4 h-4 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'}`}>
                    {isSel && <Check size={11} />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-gray-900 truncate">{u.name}</span>
                    <span className="block text-xs text-gray-500 truncate">{u.email} {u.role ? `· ${u.role}` : ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {value.length > 0 && (
            <div className="border-t border-gray-100 px-2 py-1.5 flex justify-between items-center text-xs">
              <span className="text-gray-500">{value.length} selected</span>
              <button type="button" onClick={() => onChange?.([])} className="text-blue-600 hover:underline" data-testid={`${testid}-clear-all`}>Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMemberPicker;
